import { Box, Flex, Heading, HStack, Icon, IconButton, Text } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import AppSelect from "components/AppSelect";
import { Pagination } from "components/Pagination";
import { useApi } from "hooks/useApi";
import { useCache } from "hooks/useCache";
import { useContext, useMemo, useState } from "react";
import { MdDownload } from "react-icons/md";
import { Store } from "store";
import { useUserRole } from "hooks/useUserRole";
import { TAX_ADMIN, TAX_COLLECTOR, TAX_SUPER_ADMIN } from "utils/roles";
import { ReactTable } from "../../components/ReactTable";

type Table3RowType = {
  id: number;
  institution_name: string;
  csv_file: string;
  created_at: string;
  qr_codes: string;
};

export default function TaxDataUploadManagementSection() {
  const {
    state: { user },
  } = useContext(Store);
  const userRole = useUserRole();
  const [page, setPage] = useState(1);
  const [institution, setInstitution] = useState<string>(
    user?.user?.institution_id || ""
  );
  const [pageSize, setPageSize] = useState(25);
  const { getTaxUploadData, getInstitutions } = useApi();

  const { data: logs, mutate } = useCache("getTaxUploadData", () =>
    getTaxUploadData({
      page: page,
      institution: user?.user?.institution_id || institution,
    })
  );
  const {
    data: institutions,
    isLoading: isInstitutionLoading,
  } = useCache("institutions", () => getInstitutions(1, "", ""));
  const handleJumpToPage = (pg: number) => {
    setPage(pg);
    mutate(
      getTaxUploadData({
        page: page,
        institution: user?.user?.institution_id || institution,
      })
    );
  };
  const handleSetNextPage = () => {
    if (logs.length > 0 && logs[0].num_of_pages >= page + 1) {
      setPage((prev: any) => prev + 1);
      mutate(
        getTaxUploadData({
          page: page + 1,
          institution: user?.user?.institution_id || institution,
        })
      );
    }
  };
  const handleSetPrevPage = () => {
    if (logs.length > 0 && page > 0) {
      setPage((prev: any) => prev - 1);
      mutate(
        getTaxUploadData({
          page: page + 1,
          institution: user?.user?.institution_id || institution,
        })
      );
    }
  };

  const handleDownloadAll = (downloadLink: string) => {
    const pdfUrl = downloadLink;
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.target = "_blank";
    link.download = "document.pdf"; // specify the filename
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const table3Columns = useMemo(() => {
    const table3ColumnHelper = createColumnHelper<Table3RowType>();
    return [
      table3ColumnHelper.accessor("institution_name", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="16px">
              <Text>{info.getValue<string>()}</Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Institution</Heading>
          </Flex>
        ),
        meta: { width: "200px" },
      }),
      table3ColumnHelper.accessor("created_at", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="16px">
              <Text>{info.getValue<string>()}</Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Date</Heading>
          </Flex>
        ),
        meta: { width: "150px" },
      }),
      table3ColumnHelper.accessor("id", {
        cell: (info) => (
          <HStack gap={"2px"}>
            {info.row.original?.qr_codes &&
              (userRole === TAX_SUPER_ADMIN ||
                userRole === TAX_ADMIN ||
                userRole === TAX_COLLECTOR) && (
                <IconButton
                  variant="outline"
                  colorScheme="gray_stroke_0"
                  aria-label="view"
                  size={"sm"}
                  icon={<Icon as={MdDownload as any} boxSize={4} />}
                  borderRadius={"4px"}
                  onClick={() => handleDownloadAll(info.row.original?.qr_codes)}
                />
              )}
          </HStack>
        ),
        header: (info) => (
          <Flex px="15px" py="14px">
            <Heading as="h6">Action</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
    ];
  }, [user]);

  return (
    <>
      {/* dealer management section */}
      <Flex
        flexDirection="column"
        alignItems="start"
        px={{ base: "20px", sm: "24px" }}
        minHeight={"100vh"}
      >
        <Flex gap="4px" alignSelf="stretch" flexDirection="column">
          <Flex
            justifyContent="flex-start"
            alignItems="center"
            flexDirection={{ base: "column", sm: "row" }}
          >
            <Heading size="text2xl" as="h5" fontFamily="Arimo">
              Fichiers
            </Heading>
            <></>
          </Flex>
        </Flex>
        <Box
          mt="16px"
          bg="white"
          w={{ md: "100%", base: "100%" }}
          borderRadius="10px"
        >
          <Flex
            bg="white"
            px="12px"
            py="16px"
            flexDirection={{ md: "row", base: "column" }}
          >
            <Flex
              // alignSelf={{ md: "auto", base: "stretch" }}
              flexDirection={{ base: "column", sm: "row" }}
              gap={2}
              w={{ base: "100%", md: "30%" }}
            >
              {!isInstitutionLoading &&
                userRole === TAX_SUPER_ADMIN && (
                  <AppSelect
                    height={"40px"}
                    id="institution"
                    name="institution"
                    onChange={(field: string, value: any) => {
                      mutate(
                        getTaxUploadData({
                          page: page,
                          institution: value,
                        })
                      );
                      setInstitution(value);
                    }}
                    value={institution as string}
                    placeholder="Veuillez sélectionner une institution"
                    options={institutions}
                    isMulti={false}
                    isLoading={isInstitutionLoading}
                  />
                )}
            </Flex>
          </Flex>
          <ReactTable
            size="sm"
            headerCellProps={{ bg: "white" }}
            tableProps={{
              borderColor: "neutral.600",
              borderTopWidth: "1px",
              borderStyle: "solid",
            }}
            columns={table3Columns as any}
            data={logs}
          />
          <Pagination
            pageIndex={page}
            pageSize={pageSize}
            setNextPageIndex={handleSetNextPage}
            setPrevPageIndex={handleSetPrevPage}
            setPageSize={setPageSize}
            jumpToPage={handleJumpToPage}
            numberOfPages={logs && logs.length > 0 && logs[0]?.num_of_pages}
          />
        </Box>
      </Flex>
    </>
  );
}
